package com.ijoin.ui.components

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLIFrameElement
import org.w3c.dom.MessageEvent
import org.w3c.dom.events.Event

@Composable
actual fun HtmlLandingView(
    html: String,
    modifier: Modifier,
    eventId: String?,
    onHeightChanged: ((Int) -> Unit)?,
    showHeader: Boolean,
    onBackClick: (() -> Unit)?,
    onBookClick: (() -> Unit)?,
    onShareClick: (() -> Unit)?,
    bookButtonText: String?,
    onFormSubmit: ((Map<String, Any>) -> Unit)?
) {
    val wrappedHtml = remember(html, eventId) { wrapHtmlWithBaseStyles(html, eventId) }
    val containerId = remember { "html-landing-${kotlin.random.Random.nextInt(1000000)}" }

    // Create full-screen iframe with header buttons
    DisposableEffect(containerId, wrappedHtml, showHeader, bookButtonText) {
        val container = (document.createElement("div") as HTMLDivElement).apply {
            id = containerId
            style.setProperty("position", "fixed")
            style.setProperty("top", "0")
            style.setProperty("left", "0")
            style.setProperty("width", "100%")
            style.setProperty("height", "100%")
            style.setProperty("z-index", "999")
            style.setProperty("background", "white")
        }

        // Create iframe for HTML content
        val iframe = (document.createElement("iframe") as HTMLIFrameElement).apply {
            style.setProperty("position", "absolute")
            style.setProperty("top", "0")
            style.setProperty("left", "0")
            style.setProperty("width", "100%")
            style.setProperty("height", "100%")
            style.setProperty("border", "none")
            style.setProperty("z-index", "1")
            setAttribute("frameborder", "0")
        }

        container.appendChild(iframe)

        // Create floating header with buttons if showHeader is true
        if (showHeader) {
            val header = (document.createElement("div") as HTMLDivElement).apply {
                style.setProperty("position", "absolute")
                style.setProperty("top", "16px")
                style.setProperty("left", "16px")
                style.setProperty("right", "16px")
                style.setProperty("z-index", "10")
                style.setProperty("display", "flex")
                style.setProperty("justify-content", "space-between")
                style.setProperty("align-items", "center")
                style.setProperty("background", "transparent")
                style.setProperty("pointer-events", "none")
            }

            // Back button (left side)
            val backButton = createIconButton(BACK_ICON_SVG) {
                onBackClick?.invoke()
            }
            header.appendChild(backButton)

            // Right side container for Book and Share buttons
            val rightGroup = (document.createElement("div") as HTMLDivElement).apply {
                style.setProperty("display", "flex")
                style.setProperty("align-items", "center")
                style.setProperty("gap", "12px")
                style.setProperty("pointer-events", "auto")
            }

            // Book button
            val bookButton = (document.createElement("button") as HTMLButtonElement).apply {
                textContent = bookButtonText ?: "Book"
                style.setProperty("background", "#1a1a1a")
                style.setProperty("color", "white")
                style.setProperty("border", "2px solid rgba(255, 255, 255, 0.8)")
                style.setProperty("border-radius", "20px")
                style.setProperty("padding", "12px 32px")
                style.setProperty("font-size", "14px")
                style.setProperty("font-weight", "600")
                style.setProperty("cursor", "pointer")
                style.setProperty("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.15)")
                style.setProperty("font-family", "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif")
                onclick = { onBookClick?.invoke() }
            }
            rightGroup.appendChild(bookButton)

            // Share button
            val shareButton = createIconButton(SHARE_ICON_SVG) {
                onShareClick?.invoke()
            }
            rightGroup.appendChild(shareButton)

            header.appendChild(rightGroup)
            container.appendChild(header)
        }

        document.body?.appendChild(container)

        // Load HTML content
        iframe.setAttribute("srcdoc", wrappedHtml)

        iframe.onload = {
            try {
                val bodyHeight = iframe.contentDocument?.body?.scrollHeight ?: 0
                onHeightChanged?.invoke(bodyHeight)
            } catch (e: Exception) {
                console.log("HtmlLandingView: ${e.message}")
            }
        }

        // Listen for form submissions from iframe via postMessage
        val messageHandler: (Event) -> Unit = { event ->
            val messageEvent = event as? MessageEvent
            val data = messageEvent?.data?.asDynamic()
            if (data?.type == "IJOIN_FORM_SUBMIT") {
                val formData = data.formData
                if (formData != null && onFormSubmit != null) {
                    // Convert JS object to Kotlin Map
                    val map = mutableMapOf<String, Any>()
                    val keys = js("Object.keys")(formData) as Array<String>
                    keys.forEach { key ->
                        val value = formData[key]
                        if (value != null) {
                            map[key] = value as Any
                        }
                    }
                    onFormSubmit.invoke(map)
                }
            }
        }
        window.addEventListener("message", messageHandler)

        onDispose {
            window.removeEventListener("message", messageHandler)
            container.remove()
        }
    }

    // Placeholder in Compose layout
    Box(modifier = modifier.fillMaxSize())
}

private fun createIconButton(iconHtml: String, onClick: () -> Unit): HTMLButtonElement {
    return (document.createElement("button") as HTMLButtonElement).apply {
        innerHTML = iconHtml
        style.setProperty("width", "40px")
        style.setProperty("height", "40px")
        style.setProperty("border-radius", "50%")
        style.setProperty("border", "none")
        style.setProperty("background", "rgba(255, 255, 255, 0.9)")
        style.setProperty("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.15)")
        style.setProperty("cursor", "pointer")
        style.setProperty("display", "flex")
        style.setProperty("align-items", "center")
        style.setProperty("justify-content", "center")
        style.setProperty("pointer-events", "auto")
        this.onclick = { onClick() }
    }
}

// Back arrow icon (dark color for contrast)
private const val BACK_ICON_SVG = """<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>"""

// Classic share icon with three connected dots (dark color for contrast)
private const val SHARE_ICON_SVG = """<svg width="18" height="18" viewBox="0 0 24 24" fill="#1a1a1a"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" stroke="#1a1a1a" stroke-width="1.5"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" stroke="#1a1a1a" stroke-width="1.5"/></svg>"""
